// ANANT LEADGEN PUBLIC LANDING PAGE — /leadgen/lp/:slug
// Anyone (unauth) can view this and submit a lead
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import QRCode from 'qrcode';
import html2canvas from 'html2canvas';
import { Sparkles, User, Phone, MapPin, Briefcase, Send, Loader2, Gift, Download, Share2 } from 'lucide-react';
import api from '../../utils/api';

const THEMES = {
  gold: { primary: 'from-amber-500 to-yellow-500', bg: 'from-amber-950 via-slate-900 to-black', ring: 'border-amber-400/50', hook: 'from-amber-400 to-yellow-300' },
  rose: { primary: 'from-rose-500 to-pink-500', bg: 'from-rose-950 via-slate-900 to-black', ring: 'border-rose-400/50', hook: 'from-rose-400 to-pink-300' },
  cyan: { primary: 'from-cyan-500 to-blue-500', bg: 'from-cyan-950 via-slate-900 to-black', ring: 'border-cyan-400/50', hook: 'from-cyan-400 to-blue-300' },
  emerald: { primary: 'from-emerald-500 to-teal-500', bg: 'from-emerald-950 via-slate-900 to-black', ring: 'border-emerald-400/50', hook: 'from-emerald-400 to-teal-300' },
  fuchsia: { primary: 'from-fuchsia-500 to-purple-500', bg: 'from-fuchsia-950 via-slate-900 to-black', ring: 'border-fuchsia-400/50', hook: 'from-fuchsia-400 to-purple-300' },
};

const DEFAULT_CATS = ['Integrated Business', 'Job', 'Private Work', 'Retail', 'Real Estate', 'Salon', 'Restaurant', 'Healthcare', 'Education', 'Manufacturing'];

export default function LeadGenPublicLP() {
  const { slug } = useParams();
  const [lp, setLp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [referStats, setReferStats] = useState(null);
  const [form, setForm] = useState({
    full_name: '', mobile: '', whatsapp: '', city: '', address: '', business_category: '',
    date_of_birth: '', anniversary: '', important_event_date: '', special_note: '',
  });
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Save this LP slug as referral in localStorage so post-signup claim works
  useEffect(() => { if (slug) try { localStorage.setItem('leadgen_ref_slug', slug); } catch (_) { /* ignore */ } }, [slug]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/leadgen/lp/${slug}`);
      setLp(r.data?.landing_page);
      try {
        const url = `${window.location.origin}/leadgen/lp/${slug}?ref=${slug}`;
        const d = await QRCode.toDataURL(url, { width: 256, margin: 1 });
        setQrDataUrl(d);
      } catch (_) { /* qr fail */ }
      try { const rs = await api.get('/leadgen/refer/stats'); setReferStats(rs.data); } catch (_) { /* ignore */ }
    } catch (e) {
      // 404 or fail
    } finally { setLoading(false); }
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    for (const k of ['full_name', 'mobile', 'whatsapp', 'city', 'address', 'business_category']) {
      if (!form[k] || !form[k].toString().trim()) { toast.error(`Please fill: ${k.replace(/_/g, ' ')}`); return; }
    }
    setSubmitting(true);
    try {
      await api.post('/leadgen/lead', { lp_slug: slug, ...form });
      toast.success('🎉 Details submitted successfully!');
      setSubmitted(true);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Submission failed'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>;
  if (!lp) return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-white"><div className="text-center"><div className="text-3xl mb-2">🔍</div><div>Landing page not found.</div></div></div>;

  const t = THEMES[lp.theme] || THEMES.gold;
  const cats = [...DEFAULT_CATS, ...(lp.custom_categories || [])];

  return (
    <div className={`min-h-screen bg-gradient-to-b ${t.bg} text-white`}>
      <div className="max-w-3xl mx-auto px-4 py-6">
        {lp.hero_image_url && (
          <div className={`rounded-2xl overflow-hidden border-2 ${t.ring} shadow-xl mb-4`}>
            <img src={lp.hero_image_url} alt={lp.display_name} className="w-full h-48 object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
          </div>
        )}
        <div className="text-center mb-6">
          <h1 className={`text-4xl sm:text-5xl font-black bg-gradient-to-r ${t.primary} bg-clip-text text-transparent`} data-testid="lp-display-name">{lp.display_name}</h1>
          {lp.brand_description && <p className="text-sm text-slate-300 mt-2 max-w-2xl mx-auto">{lp.brand_description}</p>}
          {/* 🔊 Loud Announcement — reads brand + offer aloud (0-cost browser TTS) */}
          <button
            type="button"
            data-testid="lp-read-aloud"
            onClick={() => {
              if (!('speechSynthesis' in window)) return toast.error('TTS not supported');
              const parts = [lp.display_name, lp.brand_description, lp.hook_offer].filter(Boolean).join('. ');
              const u = new SpeechSynthesisUtterance(parts);
              u.lang = 'hi-IN'; u.rate = 0.95;
              const voices = window.speechSynthesis.getVoices?.() || [];
              const hi = voices.find((v) => v.lang?.startsWith('hi'));
              if (hi) u.voice = hi;
              window.speechSynthesis.cancel();
              window.speechSynthesis.speak(u);
              toast.success('🔊 Sun rahe hain…');
            }}
            className={`mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r ${t.primary} text-black text-xs font-black shadow-lg`}
          >
            🔊 Sunein / Listen
          </button>
        </div>

        {lp.hook_offer && (
          <div className={`rounded-2xl border-2 ${t.ring} bg-gradient-to-r ${t.hook} p-4 mb-6 text-black`} data-testid="lp-hook-offer">
            <div className="flex items-center gap-2 font-black text-lg"><Gift className="w-5 h-5" /> {lp.hook_offer}</div>
          </div>
        )}

        {/* Refer & Earn banner (first 10K signups) */}
        {referStats?.active && (
          <div className="rounded-2xl border-2 border-amber-400/60 bg-gradient-to-r from-amber-500/30 via-pink-500/20 to-purple-500/20 p-4 mb-6" data-testid="lp-refer-banner">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-amber-200 font-black">🎁 Refer &amp; Earn · Limited Launch Offer</div>
                <div className="text-sm mt-1"><b className="text-amber-200">Sign up now &amp; get {referStats.bonus_at} AT FREE</b> — your inviter also gets {referStats.bonus_at} AT!</div>
                <div className="text-[10px] opacity-80 mt-1">Only for first {referStats.cap.toLocaleString()} signups · <b className="text-amber-200">{referStats.remaining.toLocaleString()}</b> spots left</div>
              </div>
              <a href={`/register?ref=${slug}`} data-testid="lp-refer-signup" className="px-4 py-2 rounded-xl bg-gradient-to-r from-amber-400 to-pink-500 text-black text-xs font-black">🚀 Sign up &amp; claim {referStats.bonus_at} AT</a>
            </div>
          </div>
        )}

        {submitted ? (
          <div className="rounded-2xl border-2 border-emerald-400/60 bg-emerald-500/10 p-6 text-center" data-testid="lp-success">
            <div className="text-5xl mb-2">🎉</div>
            <div className="text-2xl font-black text-emerald-300">Dhanyavaad!</div>
            <div className="text-sm text-slate-300 mt-2">Aapki details {lp.display_name} tak pahunch gayi hain. Wo aap se soon contact karenge.</div>
            <ThankYouCard lp={lp} form={form} theme={t} qrDataUrl={qrDataUrl} slug={slug} referStats={referStats} />
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-2xl border border-slate-700 bg-slate-900/70 p-4 space-y-3" data-testid="lp-form">
            <div className="text-xs uppercase tracking-widest opacity-70 mb-2">📝 Aapki details</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input required placeholder="Full Name *" value={form.full_name} onChange={(e) => upd('full_name', e.target.value)} data-testid="lp-name" className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm" />
              <input required placeholder="Mobile *" value={form.mobile} onChange={(e) => upd('mobile', e.target.value)} data-testid="lp-mobile" className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm" />
              <input required placeholder="WhatsApp *" value={form.whatsapp} onChange={(e) => upd('whatsapp', e.target.value)} data-testid="lp-whatsapp" className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm" />
              <input required placeholder="City *" value={form.city} onChange={(e) => upd('city', e.target.value)} data-testid="lp-city" className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm" />
            </div>
            <textarea required placeholder="Address *" value={form.address} onChange={(e) => upd('address', e.target.value)} data-testid="lp-address" rows={2} className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm" />
            <select required value={form.business_category} onChange={(e) => upd('business_category', e.target.value)} data-testid="lp-category" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm">
              <option value="">Select Business Category *</option>
              {cats.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <div className="text-[10px] uppercase opacity-60">Optional — Aapki khaas dates (hum aapko wish karenge)</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <label className="block text-xs">
                <span className="opacity-80 flex items-center gap-1">🎂 <b>Date of Birth</b></span>
                <input type="date" value={form.date_of_birth} onChange={(e) => upd('date_of_birth', e.target.value)} data-testid="lp-dob" className="mt-1 w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm" />
              </label>
              <label className="block text-xs">
                <span className="opacity-80 flex items-center gap-1">💍 <b>Anniversary</b></span>
                <input type="date" value={form.anniversary} onChange={(e) => upd('anniversary', e.target.value)} data-testid="lp-anniv" className="mt-1 w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm" />
              </label>
              <label className="block text-xs">
                <span className="opacity-80 flex items-center gap-1">🎉 <b>Important Event</b></span>
                <input type="date" value={form.important_event_date} onChange={(e) => upd('important_event_date', e.target.value)} data-testid="lp-event" className="mt-1 w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm" />
              </label>
            </div>
            <textarea placeholder="Special Note" value={form.special_note} onChange={(e) => upd('special_note', e.target.value)} data-testid="lp-note" rows={2} className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm" />
            <button type="submit" disabled={submitting} data-testid="lp-submit" className={`w-full px-4 py-3 rounded-xl bg-gradient-to-r ${t.primary} text-black font-black flex items-center justify-center gap-1`}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Submit Details</>}
            </button>
          </form>
        )}

        {qrDataUrl && (
          <div className="mt-6 flex flex-col items-center opacity-80">
            <img src={qrDataUrl} alt="QR" className="w-32 h-32 rounded-lg" />
            <div className="text-[10px] mt-1">Scan or share this QR</div>
          </div>
        )}
        <div className="mt-6 text-center text-[10px] text-slate-500">Powered by Anant Web Portal · LeadGen</div>
      </div>
    </div>
  );
}


// ===== Shareable Thank-You Card =====
function ThankYouCard({ lp, form, theme, qrDataUrl, slug, referStats }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const render = async () => {
    if (!cardRef.current) return null;
    const canvas = await html2canvas(cardRef.current, { backgroundColor: null, scale: 2, useCORS: true, logging: false });
    return canvas;
  };

  const download = async () => {
    setBusy(true);
    try {
      const canvas = await render();
      if (!canvas) return;
      const link = document.createElement('a');
      link.download = `${(lp.display_name || 'card').replace(/\s+/g, '_')}_thankyou.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Card downloaded!');
    } catch (e) { toast.error('Download failed'); } finally { setBusy(false); }
  };

  const share = async () => {
    setBusy(true);
    try {
      const canvas = await render();
      if (!canvas) return;
      const blob = await new Promise((r) => canvas.toBlob(r, 'image/png', 0.95));
      const file = new File([blob], 'thankyou.png', { type: 'image/png' });
      const url = `${window.location.origin}/leadgen/lp/${slug}`;
      const bonus = referStats?.active ? `\n\n🎁 *BONUS*: Sign up via my link & get ${referStats.bonus_at} AT FREE (only first ${referStats.cap.toLocaleString()} — ${referStats.remaining.toLocaleString()} spots left)!` : '';
      const shareText = `🎉 I just connected with *${lp.display_name}*!${bonus}\n\nCheck them out & get exclusive offers:\n${url}`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: lp.display_name, text: shareText });
      } else {
        // Fallback: open WhatsApp with text; user attaches image manually after download
        await download();
        window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank');
        toast.success('Image saved — attach it in WhatsApp!');
      }
    } catch (e) { toast.error('Share failed'); } finally { setBusy(false); }
  };

  return (
    <div className="mt-5" data-testid="lp-thankyou-card">
      {/* Renderable card */}
      <div ref={cardRef} className={`mx-auto max-w-md rounded-2xl border-2 ${theme.ring} bg-gradient-to-br ${theme.bg} p-5 text-white text-left shadow-2xl relative overflow-hidden`} style={{ aspectRatio: '4 / 5' }}>
        <div className={`absolute inset-0 opacity-15 bg-gradient-to-br ${theme.primary}`}></div>
        <div className="relative z-10 h-full flex flex-col justify-between">
          <div>
            <div className="text-[9px] uppercase tracking-widest opacity-75">🎉 Successfully connected with</div>
            <div className={`text-2xl font-black mt-1 bg-gradient-to-r ${theme.primary} bg-clip-text text-transparent leading-tight`}>{lp.display_name}</div>
            {lp.brand_description && <div className="text-[11px] opacity-80 mt-1 line-clamp-2">{lp.brand_description}</div>}
            {lp.hook_offer && (
              <div className={`mt-3 rounded-lg bg-gradient-to-r ${theme.hook} text-black p-2 text-[11px] font-black flex items-center gap-1`}>🎁 {lp.hook_offer}</div>
            )}
          </div>
          <div className="my-3 rounded-xl bg-white/10 backdrop-blur p-3 border border-white/15">
            <div className="text-[9px] uppercase tracking-widest opacity-70">Your Details</div>
            <div className="text-sm font-black mt-0.5">{form.full_name}</div>
            <div className="text-[11px] opacity-80">📍 {form.city} · {form.business_category}</div>
          </div>
          <div className="flex items-end justify-between gap-2">
            <div className="text-[10px] opacity-80 leading-tight">
              <div>Scan QR or share to connect</div>
              <div className="opacity-60 mt-1">— Powered by Anant Web Portal</div>
            </div>
            {qrDataUrl && <img src={qrDataUrl} alt="QR" className="w-16 h-16 rounded bg-white p-1" crossOrigin="anonymous" />}
          </div>
        </div>
      </div>
      {/* Actions */}
      <div className="flex gap-2 mt-3 justify-center flex-wrap">
        <button onClick={download} disabled={busy} data-testid="lp-card-download" className={`px-3 py-2 rounded-lg bg-white/10 text-xs font-black flex items-center gap-1 disabled:opacity-40`}>{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Download</button>
        <button onClick={share} disabled={busy} data-testid="lp-card-share" className={`px-4 py-2 rounded-lg bg-gradient-to-r ${theme.primary} text-black text-xs font-black flex items-center gap-1 disabled:opacity-40`}>{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Share2 className="w-3 h-3" />} Share Card</button>
      </div>
      <div className="text-[10px] opacity-60 mt-2">Share this card to spread the word — help {lp.display_name} grow! 💫</div>
    </div>
  );
}
